import { SITE_URL, SITE_NAME, SITE_EMAIL, SITE_ADDRESS, SOCIAL_PROFILES } from '../config/site';
import { companySummary, faqs, getProductSummaries, servicesSummary } from '../data/siteKnowledge';

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    description: companySummary.description,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    email: SITE_EMAIL,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: SITE_EMAIL,
      areaServed: 'PH',
      availableLanguage: ['English', 'Filipino'],
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'PH',
      addressRegion: 'Pampanga',
      addressLocality: SITE_ADDRESS,
    },
    sameAs: SOCIAL_PROFILES,
    foundingDate: companySummary.founded,
    knowsAbout: companySummary.expertise,
  };
}

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    description: companySummary.description,
    publisher: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'en-PH',
  };
}

export function buildLocalBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${SITE_URL}/#localbusiness`,
    name: SITE_NAME,
    url: SITE_URL,
    image: `${SITE_URL}/og-image.png`,
    description: companySummary.description,
    email: SITE_EMAIL,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'PH',
      addressRegion: 'Pampanga',
      addressLocality: SITE_ADDRESS,
    },
    areaServed: {
      '@type': 'Country',
      name: 'Philippines',
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function buildFAQSchema(faqItems = faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function buildServiceSchema(service) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.type || 'IT Service',
    name: service.name,
    description: service.description,
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: { '@type': 'Country', name: 'Philippines' },
  };
}

export function buildProductSchema(product) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: product.name,
    description: product.description,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: `${SITE_URL}/products#${product.slug}`,
    provider: { '@id': `${SITE_URL}/#organization` },
  };
}

export function buildProductCatalogSchema() {
  const products = getProductSummaries();
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${SITE_NAME} AI Products`,
    description: 'AI-powered enterprise software products by XDigital IT Consultancy',
    numberOfItems: products.length,
    itemListElement: products.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: product.name,
      url: product.url,
      description: product.tagline,
    })),
  };
}

export function buildServicesListSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${SITE_NAME} Services`,
    numberOfItems: servicesSummary.length,
    itemListElement: servicesSummary.map((service, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: service.name,
      description: service.description,
    })),
  };
}

export function buildWebPageSchema({ name, description, url }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'en-PH',
  };
}
